/**
 * shared/fairness.ts — the rule layer of the fairness engine (card
 * S1YSeaemeA2N · spec kZX3gsJkKaTH v0.2 · DECISIONS Hsrn9P845Ep2).
 *
 * Pure functions only: no DOM, no fetch, no Date.now() — the caller passes
 * `now`, which is what makes the thresholds unit-testable from a bare `node`
 * (scripts/probe-chores-fairness.mjs, phase `unit`), the same shape as
 * shared/life-rail.ts.
 *
 * The trigger is LAG, not split. An uneven split that both people clear is an
 * ARRANGEMENT, not an imbalance, so a volume ratio never fires anything here.
 * What fires is work that has been genuinely stuck for weeks, behind two
 * minimums (21 days of history, 8 assigned occurrences per member) and the
 * silence rule: both sides at 85% or better means NO card at any volume split.
 *
 * Nothing in this module renders. The counts it computes are inputs to one
 * judgement and never reach a client — the user-facing route strips them
 * (worker/src/api/chores.ts, GET /api/chores/fairness).
 */

/** One day in ms — the unit every bar below is written in. */
export const FAIRNESS_DAY_MS = 86_400_000;

/** Nothing is user-facing before this much chore history exists (spec §2). */
export const FAIRNESS_HISTORY_DAYS = 21;

/** Per member: fewer assigned occurrences than this and their score is null. */
export const FAIRNESS_MIN_ASSIGNED = 8;

/** Branch (a): the same chore past due for this many consecutive occurrences. */
export const FAIRNESS_MISS_BAR = 3;

/** Branch (b): below this while the partner is above FAIRNESS_HIGH. */
export const FAIRNESS_LOW = 0.6;

/** Both sides at or above this = consistency buys silence (spec §2). */
export const FAIRNESS_HIGH = 0.85;

/** A completed occurrence: who did it, when, and whether it landed late.
 *  `dueAt` is NULL for a backfilled recurring row — that row's due date was
 *  overwritten by the roll-forward, so its lateness is genuinely unknown and
 *  reads as on-time rather than being guessed (the same NULL contract
 *  rowToChore keeps). */
export interface FairnessCompletion {
  choreId: string;
  memberId: string;
  completedAt: number;
  dueAt: number | null;
  wasLate: boolean;
}

/** An occurrence past due and still open (spec §1). Only 'fixed' rows are
 *  tracked: 'alternate' hands the pointer over on completion alone and
 *  'claim' has no owner, so drift cannot accumulate in either. */
export interface FairnessLag {
  choreId: string;
  firstMissedAt: number;
  misses: number;
  assigneeId: string | null;
  assignMode: string;
}

/** The slice of a chore the rule layer reads. */
export interface FairnessChore {
  choreId: string;
  title: string;
  cadence: string;
  freq: string | null;
  recurMode: string | null;
  recurEveryDays: number | null;
  assignMode: string;
}

export interface FairnessInput {
  entries: readonly FairnessCompletion[];
  lag: readonly FairnessLag[];
  chores: readonly FairnessChore[];
  /** The couple's two member ids — reliability is scored per member. */
  members: readonly string[];
  now: number;
}

export interface FairnessEvaluation {
  /** Enough history AND every member past the occurrence minimum. */
  eligible: boolean;
  /** Whether the engine may speak at all (see reason for why not). */
  fires: boolean;
  reason: 'lag' | 'reliability' | null;
  /** Why it stayed quiet, for the probe and the logs — never a client. */
  quiet: null | 'history' | 'minimum' | 'alternating' | 'consistent' | 'no-chore';
  /** The chore the card would name (branch a) — null when nothing lags. */
  choreId: string | null;
  /** Whose name the lagging chore carries. */
  laggingMemberId: string | null;
  /** Per member: on-time share of their OWN assigned occurrences, or null
   *  while they are under the minimum. Stripped before any client read. */
  reliability: Record<string, number | null>;
}

/** The period one occurrence of this chore covers — the unit "3 consecutive
 *  missed occurrences" counts in. */
export function fairnessPeriodMs(chore: FairnessChore): number {
  if (chore.cadence !== 'recurring') return FAIRNESS_DAY_MS;
  if (chore.recurMode === 'interval' || chore.recurMode === 'completion') {
    return Math.max(1, chore.recurEveryDays ?? 1) * FAIRNESS_DAY_MS;
  }
  if (chore.freq === 'daily') return FAIRNESS_DAY_MS;
  if (chore.freq === 'monthly') return 30 * FAIRNESS_DAY_MS;
  return 7 * FAIRNESS_DAY_MS;
}

/** Occurrences missed so far: 0 while it is still in time, 1 the moment it is
 *  late (the occurrence itself went by), then one more per whole period. */
export function missedOccurrences(dueAt: number | null, now: number, periodMs: number): number {
  if (dueAt == null || now <= dueAt) return 0;
  return Math.floor((now - dueAt) / Math.max(1, periodMs)) + 1;
}

/** Default effort weight (spec §5): the seed categories Pear already uses
 *  when it writes a chore for a couple. Admin at 2x is deliberate — counting
 *  "Plan the week" as equal to a bin run biases the engine towards whoever
 *  is dodging the invisible work. Custom titles default to 2. */
const HEAVY = ['mop the floor', 'vacuum', 'clean the oven', 'clean the fridge', 'mow the lawn', 'clean the bathroom', 'window'];
const ADMIN = ['plan the week', 'sort the paperwork', 'pay a bill', 'restock the fridge'];
const LIGHT = ['bin', 'wipe the bench', 'feed the cat', 'water the plant'];

export function choreWeight(title: string): number {
  const t = title.toLowerCase();
  if (LIGHT.some(k => t.includes(k))) return 1;
  if (ADMIN.some(k => t.includes(k))) return 2;
  if (HEAVY.some(k => t.includes(k))) return 3;
  return 2;
}

/** Reliability: the share of a member's OWN assigned occurrences that landed
 *  on time. Assigned = what they completed + what is late with their name on
 *  it, so a chore nobody has ticked off still counts against the person
 *  holding it. Null while under FAIRNESS_MIN_ASSIGNED — no score, no card. */
function reliabilityOf(
  memberId: string,
  entries: readonly FairnessCompletion[],
  lag: readonly FairnessLag[],
): number | null {
  let assigned = 0;
  let onTime = 0;
  for (const e of entries) {
    if (e.memberId !== memberId) continue;
    assigned += 1;
    if (!e.wasLate) onTime += 1;
  }
  for (const l of lag) {
    if (l.assigneeId === memberId) assigned += 1;
  }
  if (assigned < FAIRNESS_MIN_ASSIGNED) return null;
  return assigned === 0 ? null : onTime / assigned;
}

/** The one rule layer: does work look genuinely stuck, and on what? */
export function evaluateFairness(input: FairnessInput): FairnessEvaluation {
  const { entries, lag, chores, members, now } = input;

  const reliability: Record<string, number | null> = {};
  for (const m of members) reliability[m] = reliabilityOf(m, entries, lag);

  const base = {
    choreId: null as string | null,
    laggingMemberId: null as string | null,
    reliability,
  };

  // Bar 1: 21 days of chore history.
  let oldest = Number.POSITIVE_INFINITY;
  for (const e of entries) oldest = Math.min(oldest, e.completedAt);
  if (!Number.isFinite(oldest) || now - oldest < FAIRNESS_HISTORY_DAYS * FAIRNESS_DAY_MS) {
    return { eligible: false, fires: false, reason: null, quiet: 'history', ...base };
  }
  // Bar 2: 8 assigned occurrences per member.
  if (members.some(m => reliability[m] == null)) {
    return { eligible: false, fires: false, reason: null, quiet: 'minimum', ...base };
  }

  // Silence rule: high reliability on BOTH sides means no card at any split.
  const scores = members.map(m => reliability[m] as number);
  if (scores.every(s => s >= FAIRNESS_HIGH)) {
    return { eligible: true, fires: false, reason: null, quiet: 'consistent', ...base };
  }

  // Drift cannot accumulate on 'alternate' or 'claim' rows, so a couple whose
  // work is all on those rules never meets the engine.
  if (!chores.some(c => c.assignMode === 'fixed')) {
    return { eligible: true, fires: false, reason: null, quiet: 'alternating', ...base };
  }
  // And a fire always needs a named chore to move, so nothing lags = no card.
  const fixedLag = lag.filter(l => l.assignMode === 'fixed' && l.assigneeId != null);
  if (fixedLag.length === 0) {
    return { eligible: true, fires: false, reason: null, quiet: 'no-chore', ...base };
  }

  // Branch (a): one chore past due for 3 consecutive occurrences, held 21+ days.
  const stuck = fixedLag
    .filter(l => l.misses >= FAIRNESS_MISS_BAR && now - l.firstMissedAt >= FAIRNESS_HISTORY_DAYS * FAIRNESS_DAY_MS)
    .sort((x, y) => y.misses - x.misses || x.firstMissedAt - y.firstMissedAt);
  if (stuck.length > 0) {
    const worst = stuck[0];
    return {
      eligible: true, fires: true, reason: 'lag', quiet: null,
      choreId: worst.choreId, laggingMemberId: worst.assigneeId, reliability,
    };
  }

  // Branch (b): one member under 60% while the partner sits over 85%.
  const lagging = members.find(m => (reliability[m] as number) < FAIRNESS_LOW);
  const steady = members.find(m => m !== lagging && (reliability[m] as number) >= FAIRNESS_HIGH);
  if (lagging && steady) {
    const worst = [...fixedLag].sort((x, y) => y.misses - x.misses || x.firstMissedAt - y.firstMissedAt)[0];
    return {
      eligible: true, fires: true, reason: 'reliability', quiet: null,
      choreId: worst.choreId, laggingMemberId: lagging, reliability,
    };
  }

  return { eligible: true, fires: false, reason: null, quiet: 'no-chore', ...base };
}

/** Slice C (spec §3): at most one card per couple per this long, whatever it
 *  was answered with. The clock starts at the card's creation, so a dismissed
 *  card still buys the couple a quiet week. */
export const FAIRNESS_CARD_COUPLE_MS = 7 * FAIRNESS_DAY_MS;

/** One chore is never offered again inside this window — whether the couple
 *  moved it or said "Not now" (locked: 45 days per chore, 1 card per couple
 *  per 7 days, DECISIONS Hsrn9P845Ep2). */
export const FAIRNESS_CARD_CHORE_QUIET_MS = 45 * FAIRNESS_DAY_MS;

/** A card nobody answered stops being offerable after this long — it expires
 *  rather than sitting in the rail until someone finally notices it. */
export const FAIRNESS_CARD_TTL_MS = 7 * FAIRNESS_DAY_MS;

/** The rail is drawn in the couple's own daytime (spec §3, 08:00–21:00). The
 *  Durable Object has no timezone to read, and the card is in-feed rather than
 *  a push, so the window is applied where the clock IS local: the client. An
 *  hour is 0–23; the window is half-open, so 21:00 is already evening. */
export function fairnessCardHourOpen(hour: number): boolean {
  return hour >= 8 && hour < 21;
}

export interface FairnessProposal {
  choreId: string;
  /** The chore's current holder — the one the offer moves it away from. */
  fromMemberId: string;
  toMemberId: string;
  score: number;
}

/** Rule fallback (spec §4, no model call): the highest weight x most misses
 *  held by the lagging member, moved to the other one. Evaluation is rule
 *  based and free; this is what the card uses whenever the agent is not
 *  wired up or its answer fails validation.
 *
 *  `quietChoreIds` (slice C) drops chores that are inside their 45-day quiet
 *  window, so the proposal walks down to the next stuck chore instead of
 *  naming one the couple has already been asked about. */
export function ruleProposal(
  input: FairnessInput,
  evaluation: FairnessEvaluation,
  quietChoreIds: ReadonlySet<string> = new Set(),
): FairnessProposal | null {
  if (!evaluation.fires || evaluation.choreId == null || evaluation.laggingMemberId == null) return null;
  const from = evaluation.laggingMemberId;
  const target = input.members.find(m => m !== from);
  if (target == null) return null;

  let best: FairnessProposal | null = null;
  for (const l of input.lag) {
    if (l.assignMode !== 'fixed' || l.assigneeId !== from) continue;
    if (quietChoreIds.has(l.choreId)) continue;
    const chore = input.chores.find(c => c.choreId === l.choreId);
    if (!chore) continue;
    const score = choreWeight(chore.title) * Math.max(1, l.misses);
    if (!best || score > best.score) {
      best = { choreId: l.choreId, fromMemberId: from, toMemberId: target, score };
    }
  }
  return best;
}

/** Slice C (spec §3): the two caps that decide whether the engine may speak
 *  again at all — one card per couple per FAIRNESS_CARD_COUPLE_MS, and this
 *  chore quiet for FAIRNESS_CARD_CHORE_QUIET_MS after its own last card.
 *  Both are pure so the probe can pin them without a Durable Object. */
export function fairnessCardAllowed(input: {
  lastCardAt: number | null;
  choreQuietUntil: number | null;
  now: number;
}): boolean {
  const { lastCardAt, choreQuietUntil, now } = input;
  if (lastCardAt != null && now - lastCardAt < FAIRNESS_CARD_COUPLE_MS) return false;
  if (choreQuietUntil != null && now < choreQuietUntil) return false;
  return true;
}

/** Phase F (spec §F): the kill-gate — a card that has to earn its place. The
 *  acceptance rate is read over this rolling window, the four weeks the spec
 *  writes the gate in. */
export const FAIRNESS_KILL_WINDOW_MS = 28 * FAIRNESS_DAY_MS;

/** A full window of evidence before anything stops. One card per couple per
 *  FAIRNESS_CARD_COUPLE_MS is the cap, so four answered cards in four weeks
 *  is every card the engine could have shown. A thinner sample — one early
 *  "Not now" — must never shut the engine off: the gate is for couples who
 *  ROUTINELY ignore the card (spec), not for one who answered once. */
export const FAIRNESS_KILL_MIN_CARDS = 4;

/** Under this share of the window's answered cards accepted, the cards stop. */
export const FAIRNESS_KILL_ACCEPT_RATE = 0.2;

/** One fairness_card as the gate reads it. */
export interface FairnessCardOutcome {
  createdAt: number;
  /** 'pending' is not counted: an unanswered card has not been refused by
   *  anyone, and it is about to be expired or answered anyway. */
  status: 'pending' | 'accepted' | 'dismissed' | 'expired';
}

/** Phase F: has the couple ignored the cards? Pure, so the probe can walk the
 *  window from a bare node import — the caller supplies `now`, and after a
 *  deliberate re-arm it supplies `since`, which is what keeps the gate from
 *  ever re-arming ITSELF: only cards created after `since` count, and the
 *  caller writes that timestamp when somebody makes the decision. */
export function fairnessKillTripped(input: {
  cards: readonly FairnessCardOutcome[];
  now: number;
  since?: number | null;
}): { tripped: boolean; shown: number; accepted: number; rate: number | null } {
  const from = Math.max(input.now - FAIRNESS_KILL_WINDOW_MS, input.since ?? 0);
  const answered = input.cards.filter(c => c.createdAt > from && c.status !== 'pending');
  const shown = answered.length;
  if (shown < FAIRNESS_KILL_MIN_CARDS) return { tripped: false, shown, accepted: 0, rate: null };
  const accepted = answered.filter(c => c.status === 'accepted').length;
  const rate = accepted / shown;
  return { tripped: rate < FAIRNESS_KILL_ACCEPT_RATE, shown, accepted, rate };
}
